import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import {
  loadAccountPreference,
  saveAccountPreference,
  onAuthPreferenceRefresh,
} from "@/contexts/accountPreference";

type FontSize = "compact" | "normal" | "large" | "xl";

interface FontSizeContextType {
  fontSize: FontSize;
  cycleFontSize: () => void;
  resetFontSize: () => void;
  label: string;
}

const FontSizeContext = createContext<FontSizeContextType>({
  fontSize: "normal",
  cycleFontSize: () => {},
  resetFontSize: () => {},
  label: "A",
});

const CYCLE: FontSize[] = ["compact", "normal", "large", "xl"];
const LABELS: Record<FontSize, string> = { compact: "A-", normal: "A", large: "A+", xl: "A++" };
const STORAGE_KEY = "behtari_font_size";
const DB_COLUMN = "font_size";

const isFontSize = (v: unknown): v is FontSize =>
  typeof v === "string" && (CYCLE as string[]).includes(v);

export const FontSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fontSize, setFontSize] = useState<FontSize>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return isFontSize(stored) ? stored : "normal";
    } catch {
      return "normal";
    }
  });

  /**
   * Guards the hydrate-vs-user-choice race.
   *
   * A COUNTER, NOT A LATCH (fixed 2026-09-06). This was a one-shot
   * `hydratedRef` boolean set to true as soon as the FIRST hydrate finished —
   * including the one that ran at mount on a signed-OUT page, where
   * `loadAccountPreference` correctly returns null because there is no session.
   * The latch closed on that null, so the `SIGNED_IN` refresh that fires a
   * moment later returned early at its own guard and the account value was
   * never read. Net effect: sign in on a freshly loaded `/login` and the
   * preference did not come back — the exact symptom this account-backing was
   * added to cure, on the one path that matters most.
   *
   * Every hydrate attempt and every explicit user action bumps the counter; an
   * in-flight hydrate applies its result only if nothing has happened since it
   * started. That keeps the original race guard (a user choice mid-hydrate
   * still wins) while letting a LATER hydrate — the sign-in one — actually run.
   */
  const generationRef = useRef(0);

  // Apply the class and mirror to localStorage for an instant, flash-free
  // paint on the next load. localStorage remains a CACHE here — the account
  // row is the source of truth, which is what makes the value survive
  // `UserContext.logout`'s deliberate `localStorage.clear()`.
  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove("font-size-compact", "font-size-normal", "font-size-large", "font-size-xl");
    html.classList.add(`font-size-${fontSize}`);
    try {
      localStorage.setItem(STORAGE_KEY, fontSize);
    } catch {
      /* private mode / blocked storage — the class above is already applied */
    }
  }, [fontSize]);

  const hydrateFromAccount = useCallback(async () => {
    const generation = ++generationRef.current;
    const read = await loadAccountPreference<FontSize>(DB_COLUMN, isFontSize);
    // Superseded by a later hydrate or by a user action while we awaited.
    if (generationRef.current !== generation) return;
    if (read.status === "value") setFontSize(read.value);
    // The account was read successfully and holds no size, so nothing was ever
    // chosen: fall back to the default rather than trusting a cached value that
    // may belong to a previous user of this device.
    else if (read.status === "absent") setFontSize("normal");
  }, []);


  useEffect(() => {
    void hydrateFromAccount();
    return onAuthPreferenceRefresh(
      () => { void hydrateFromAccount(); },
      // Invalidate any hydrate still in flight for the account being left, so
      // its result cannot land on the next user's screen, and return to the
      // default so the next person does not inherit this one's text size.
      () => { generationRef.current++; setFontSize("normal"); },
    );

  }, [hydrateFromAccount]);

  const applyFontSize = useCallback((next: FontSize) => {
    generationRef.current++; // an explicit choice outranks a pending hydrate
    setFontSize(next);
    void saveAccountPreference(DB_COLUMN, next);
  }, []);

  /**
   * Routes through `applyFontSize` so the two write paths behave identically.
   *
   * HARDENING, NOT A PROVEN BUG FIX (2026-09-07) — stated plainly so nobody
   * later cites this as the cause of the "text resets to A+" report. It is not.
   * That report was investigated and the confirmed finding was elsewhere: three
   * accounts (admin@behtari.test among them) genuinely hold `font_size='large'`
   * in `user_preferences`, all three written on 2026-09-06, so those accounts
   * correctly open at A+. Writes were verified to be landing. The "it snaps
   * back after I change it" half could NOT be reproduced.
   *
   * What was wrong here is narrower and worth fixing on its own terms: this used
   * to mutate `generationRef` and fire a network write from inside the
   * `setFontSize` updater. React runs updaters during the RENDER phase and does
   * not promise to run them exactly once, so (a) the guard meant to make a tap
   * outrank an in-flight hydrate was itself deferred past the click, unlike
   * `applyFontSize` which bumps it synchronously, and (b) a replayed render
   * could issue the account write twice. Two paths that should be the same thing
   * were subtly different, and the one wired to the visible button was the
   * weaker of the two.
   *
   * A test was written to catch the deferred-guard race and is deliberately NOT
   * in the suite: under `act()` React flushes the updater before a released
   * promise resolves, so it passed against the old code as well and proved
   * nothing. Do not add one that cannot fail against the pre-fix implementation.
   */
  const cycleFontSize = useCallback(() => {
    applyFontSize(CYCLE[(CYCLE.indexOf(fontSize) + 1) % CYCLE.length]);
  }, [fontSize, applyFontSize]);

  const resetFontSize = useCallback(() => applyFontSize("normal"), [applyFontSize]);

  return (
    <FontSizeContext.Provider value={{ fontSize, cycleFontSize, resetFontSize, label: LABELS[fontSize] }}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => useContext(FontSizeContext);
